/**
=========================================================
* Argon Dashboard 2 MUI - v3.0.1
=========================================================

* Product Page: https://www.creative-tim.com/product/argon-dashboard-material-ui
* Copyright 2023 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

// @mui material components
import Card from "@mui/material/Card";
// Argon Dashboard 2 MUI components
import ArgonBox from "components/ArgonBox";
import ArgonTypography from "components/ArgonTypography";

// Argon Dashboard 2 MUI examples
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import Table from "examples/Tables/Table";
import Grid from "@mui/material/Grid";
import ArgonButton from "components/ArgonButton";
import { Chip } from "@mui/material";

// Data
import CreditMappingViewTableData from "Views/Activities/CreditMapping/data/creditMappingViewTableData";
import CreditMappingViewCreditTableData from "Views/Activities/CreditMapping/data/creditMappingViewCreditTableData";

// import ReferralTableData from "Views/Activities/Referral/data/ReferralTableData"
import { useState, useEffect } from "react";
import { set } from "date-fns";
import Dropdown from "components/CustomDropdown/Dropdown";
import ArgonInput from "components/ArgonInput";
import { useParams, useNavigate } from "react-router-dom";
import api from "utils/api";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { fetchAllPages } from "utils/fetchAllPages";
import { mapInvoicesToCycles, getCyclePaymentStatus } from "utils/cycleInvoiceMapping";

function Tables() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [invoiceData, setInvoiceData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [cycle, setCycle] = useState("");

  const toSafeNumber = (value) => {
    const number = Number(value);
    return Number.isFinite(number) ? number : 0;
  };

  useEffect(() => {
    fetchInvoiceDetails();
  }, [id]);

  useEffect(() => {
    const currentCycle = invoiceData?.classSessionId?.[0]?.currentCycle;

    if (currentCycle) {
      setCycle(currentCycle);
    }
  }, [invoiceData]);

  const fetchInvoiceDetails = async () => {
    try {
      setLoading(true);
      setError(null);

      // Since there's no single invoice endpoint, fetch all invoice pages and filter by ID.
      const invoices = await fetchAllPages(api, "/finance/get-all-raise-invoice");

      if (Array.isArray(invoices)) {
        // Find the invoice with matching ID
        const invoice = invoices.find(
          (inv) =>
            inv._id === id || inv.classSessionId?.[0]?._id === id || inv.classSessionDoc?._id === id ||
            inv.invoices?.some((item) => item._id === id)
        );

        if (invoice) {
          setInvoiceData(invoice);

          const currentCycle = invoice?.classSessionId?.[0]?.currentCycle;

          if (currentCycle) {
            setCycle(currentCycle);
          }
        } else {
          setError("Invoice not found");
        }
      } else {
        setError("Failed to fetch invoice details");
      }
    } catch (err) {
      setError(err.message || "Failed to fetch invoice details");
      console.error("Error fetching invoice:", err);
    } finally {
      setLoading(false);
    }
  };

  const classSession = invoiceData?.classSessionId?.[0] || invoiceData?.classSessionDoc;

  const cycles = classSession?.cycles || [];
  // Generate dynamic rows for Credit Details from cycles
  const totalCycles = cycles.length;

  const currentCycleName = cycle || classSession?.currentCycle || cycles?.[0]?.name;

  const selectedCycleData = cycles.find((item) => item.name === currentCycleName) || cycles?.[0];

  const cycleIndex = cycles.findIndex((item) => item.name === currentCycleName);

  const getCycleNumber = (cycleData, cycleIdx) =>
    Number(String(cycleData?.name || "").match(/\d+/)?.[0]) || cycleIdx + 1;
  const cycleInvoiceMappings = mapInvoicesToCycles(cycles, (invoiceData?.invoices || []).filter(item => item.invoiceType !== "ADDITIONAL"));
  const selectedCycleIndex = cycleIndex >= 0 ? cycleIndex : 0;
  const revisionForCycle = index => (invoiceData?.feeRevisionSummary || classSession?.feeRevisionSummary)?.cycles?.find(item => item.cycleIndex === index);
  const selectedRevision = revisionForCycle(selectedCycleIndex);
  const selectedMapping = cycleInvoiceMappings[selectedCycleIndex];
  const selectedInvoice = selectedMapping?.invoice;
  const selectedBaseAmount = toSafeNumber(
    selectedCycleData?.monthlyFee || selectedCycleData?.paymentAmount ||
    selectedMapping?.amount || classSession?.paymentAmount
  );
  const selectedCycleAmount = selectedBaseAmount + toSafeNumber(selectedRevision?.additionalFee);
  const selectedPaymentStatus = selectedRevision?.additionalPending > 0 ? "PENDING" : getCyclePaymentStatus(
    selectedCycleData, selectedMapping,
    invoiceData?.invoices?.length ? undefined : classSession?.paymentStatus
  );
  const selectedPaymentMethod = selectedInvoice?.paymentMethod ||
    selectedCycleData?.paymentMethod ||
    (!invoiceData?.invoices?.length ? classSession?.paymentMethod || invoiceData?.paymentMethod : null) || "N/A";

  // Calculate progress
  const completedSessions = selectedCycleData?.completedClasses || 0;

  const totalSessions = selectedCycleData?.totalNoOfClasses || 0;
  const progress = totalSessions > 0 ? (completedSessions / totalSessions) * 100 : 0;

  const safeProgress = Math.max(0, Math.min(100, Math.round(progress)));
  const CIRCLE_R = 46;
  const circumference = 2 * Math.PI * CIRCLE_R;
  const strokeDasharray = `${circumference}px`;
  const strokeDashoffset = `${circumference * (1 - safeProgress / 100)}px`;

  const baseFeeCharge =
    totalSessions > 0
      ? toSafeNumber(selectedCycleData?.monthlyFee || 0) / totalSessions
      : toSafeNumber(classSession?.feeCharge);

  const feeCharge = baseFeeCharge + toSafeNumber(selectedRevision?.additionalPerClass);
  const currentTutorRate =
    Number(classSession?.tutorSalary) ||
    Number(invoiceData?.tutorPayRate) ||
    Number(selectedInvoice?.tutorPayRate) ||
    0;

  const tutorSalary = selectedRevision?.tutorPerClassSalary ?? (
    currentTutorRate > 0
      ? currentTutorRate
      : totalSessions > 0
      ? Number(selectedCycleData?.tutorPay || 0) / totalSessions
      : 0);

  const sessionsPerWeek = classSession?.numberOfClassesInAWeek || classSession?.weeklyClasses || 0;
  const hoursPerSession = classSession?.duration ? classSession.duration / 60 : 1.5;
  const totalPackageClasses = classSession?.totalNoOfClasses || classSession?.totalClasses || 0;
  const noOfMonths = classSession?.noOfMonths || 1;

  let sessionsPerMonth = 0;

  if (noOfMonths > 0 && totalPackageClasses > 0) {
    sessionsPerMonth = Math.round(totalPackageClasses / noOfMonths);
  } else if (sessionsPerWeek > 0) {
    sessionsPerMonth = Math.round(sessionsPerWeek * 4);
  } else if (totalSessions > 0 && noOfMonths > 0) {
    sessionsPerMonth = Math.round(totalSessions / noOfMonths);
  }

  const usedAmount =
    totalSessions > 0 ? Math.floor((completedSessions / totalSessions) * selectedBaseAmount + toSafeNumber(selectedRevision?.additionalUsed)) : 0;

  const oldTutorDetails = invoiceData?.oldTutorDetails || null;
  const oldTutorName = oldTutorDetails?.fullName || oldTutorDetails?.name || "N/A";
  const oldTutorSalary = invoiceData?.oldTutorPerClassSalary ?? 0;
  const oldTutorTotalSalary = invoiceData?.oldTutorCalculatedAmount ?? 0;
  const oldTutorCompletedClasses = invoiceData?.oldTutorCompletedClasses ?? 0;
  const oldTutorSalaryStatus = invoiceData?.oldTutorSalaryStatus || "N/A";
  const oldTutorReplacedAt = invoiceData?.oldTutorReplacedAt || null;

  const getCycleCharge = (cycleData, cycleIdx) => {
    const cycleClasses = Number(cycleData?.totalNoOfClasses || 0);
    return Math.round(
      (toSafeNumber(cycleData?.monthlyFee) ||
        toSafeNumber(cycleInvoiceMappings[cycleIdx]?.amount) ||
        (cycleClasses > 0 ? baseFeeCharge * cycleClasses : 0)) + toSafeNumber(revisionForCycle(cycleIdx)?.additionalFee)
    );
  };

  const getCycleTutorPay = (cycleData, cycleIdx) => {
    const revision = revisionForCycle(cycleIdx);
    if (revision?.projectedTutorPay != null) return revision.projectedTutorPay;
    const cycleSalary = revision?.tutorPerClassSalary ?? (revision ? currentTutorRate : tutorSalary);
    const cycleClasses = Number(cycleData?.totalNoOfClasses || 0);
    const fallbackTutorPay = Math.round(
      toSafeNumber(cycleData?.tutorPay) || (cycleClasses > 0 ? cycleClasses * cycleSalary : 0)
    );

    const cycleNumber = Number(String(cycleData?.name || "").match(/\d+/)?.[0]) || cycleIdx + 1;
    const oldTutorCyclesForThisCycle = Array.isArray(invoiceData?.oldTutorPaymentCycles)
      ? invoiceData.oldTutorPaymentCycles.filter(
          (paymentCycle) => Number(paymentCycle?.cycleNumber) === cycleNumber
        )
      : [];

    if (oldTutorCyclesForThisCycle.length > 0) {
      const oldTutorClassesInCycle = oldTutorCyclesForThisCycle.reduce(
        (sum, paymentCycle) => sum + toSafeNumber(paymentCycle?.completedClasses),
        0
      );
      const oldTutorPayInCycle = oldTutorCyclesForThisCycle.reduce((sum, paymentCycle) => {
        const calculatedAmount = toSafeNumber(paymentCycle?.calculatedAmount);
        return (
          sum +
          (calculatedAmount > 0
            ? calculatedAmount
            : toSafeNumber(paymentCycle?.tutorPayRate) *
              toSafeNumber(paymentCycle?.completedClasses))
        );
      }, 0);
      const remainingClasses = Math.max(cycleClasses - oldTutorClassesInCycle, 0);

      return Math.round(oldTutorPayInCycle + remainingClasses * cycleSalary);
    }

    return fallbackTutorPay;
  };
  const monthCharge = getCycleCharge(selectedCycleData, selectedCycleIndex);
  const totalCharge =
    cycles.length > 0
      ? cycles.reduce((sum, cycleItem, idx) => sum + getCycleCharge(cycleItem, idx), 0)
      : Math.round(toSafeNumber(invoiceData?.totalAmount));

  const totalTutorPay =
    cycles.length > 0
      ? cycles.reduce((sum, cycleItem, idx) => sum + getCycleTutorPay(cycleItem, idx), 0)
      : Math.round(
          toSafeNumber(invoiceData?.totalTutorCost) ||
            toSafeNumber(invoiceData?.totalTutorPay) ||
            tutorSalary * totalPackageClasses
        );

  const totalProfit = totalCharge - totalTutorPay;
  const totalProfitPercent = totalCharge > 0 ? Math.round((totalProfit / totalCharge) * 100) : 0;

  const selectedCycleClasses =
    Number(selectedCycleData?.totalNoOfClasses || 0) || totalSessions || sessionsPerMonth || 0;
  const monthTutorPay = getCycleTutorPay(selectedCycleData, selectedCycleIndex);
  const monthProfit = monthCharge - monthTutorPay;
  const monthProfitPercent = monthCharge > 0 ? Math.round((monthProfit / monthCharge) * 100) : 0;

  const classStatus = classSession?.status || "N/A";
  const tutorName = invoiceData?.tutorId?.fullName || "N/A";

  // Match the parent's fee to the exact number of classes taught by the old tutor.
  const oldTutorClassesFee = baseFeeCharge * oldTutorCompletedClasses;
  const oldTutorMargin =
    oldTutorClassesFee > 0
      ? Math.round(((oldTutorClassesFee - oldTutorTotalSalary) / oldTutorClassesFee) * 100)
      : null;

  const oldTutorReplacedDateLabel = oldTutorReplacedAt
    ? new Date(oldTutorReplacedAt).toLocaleDateString()
    : "N/A";

  const hasOldTutorReplacement = Boolean(
    oldTutorReplacedAt || oldTutorDetails?.fullName || invoiceData?.oldTutorId
  );

  const balanceAmount = Math.max(0, selectedCycleAmount - usedAmount);

  const studentsData = invoiceData?.students || [];

  const { columns } = CreditMappingViewTableData;

  // Generate dynamic rows from studentsData
  const rows = studentsData.map((student, index) => ({
    Sno: (
      <ArgonTypography variant="caption" color="text" fontWeight="medium">
        {index + 1}
      </ArgonTypography>
    ),
    "Student name": (
      <ArgonTypography variant="caption" color="text" fontWeight="medium">
        {student.name || "N/A"}
      </ArgonTypography>
    ),
    Class: (
      <ArgonTypography variant="caption" color="text" fontWeight="medium">
        {student.class?.name || "N/A"}
      </ArgonTypography>
    ),
    subject: (
      <ArgonTypography variant="caption" color="text" fontWeight="medium">
        {student.subjects && student.subjects.length > 0 ? student.subjects.join(", ") : "N/A"}
      </ArgonTypography>
    ),
    Gender: (
      <ArgonTypography variant="caption" color="text" fontWeight="medium">
        {student.gender || "N/A"}
      </ArgonTypography>
    ),
    "Date of Birth": (
      <ArgonTypography variant="caption" color="text" fontWeight="medium">
        {student.dob ? new Date(student.dob).toLocaleDateString() : "N/A"}
      </ArgonTypography>
    ),
    "School/college/university Name": (
      <ArgonTypography variant="caption" color="text" fontWeight="medium">
        {student.schoolName?.name || student.schoolName || student.school || "N/A"}
      </ArgonTypography>
    ),
    Board: (
      <ArgonTypography variant="caption" color="text" fontWeight="medium">
        {student.board?.name || "N/A"}
      </ArgonTypography>
    ),
    "Preferred Time": (
      <ArgonTypography variant="caption" color="text" fontWeight="medium">
        {student.preferredTimeDuration ? `${student.preferredTimeDuration} mins` : "N/A"}
      </ArgonTypography>
    ),
    "Tuition Tenure": (
      <ArgonTypography variant="caption" color="text" fontWeight="medium">
        {student.noOfClassesPerWeek ? `${student.noOfClassesPerWeek} classes/week` : "N/A"}
      </ArgonTypography>
    ),
    "Location Preference": (
      <ArgonTypography variant="caption" color="text" fontWeight="medium">
        N/A
      </ArgonTypography>
    ),
    "Location City": (
      <ArgonTypography variant="caption" color="text" fontWeight="medium">
        {student.parent?.city || "N/A"}
      </ArgonTypography>
    ),
    "Tutors Level": (
      <ArgonTypography variant="caption" color="text" fontWeight="medium">
        {invoiceData?.tutorId?.tutorLevel || "N/A"}
      </ArgonTypography>
    ),
  }));
  const { columns: columns1 } = CreditMappingViewCreditTableData;

  const rows1 = cycles.map((cycle, index) => ({
    "Cycle Name": (
      <ArgonTypography variant="caption" color="text" fontWeight="medium">
        {cycle.name || `Cycle ${index + 1}`}
      </ArgonTypography>
    ),
    "Total Classes": (
      <ArgonTypography variant="caption" color="text" fontWeight="medium">
        {cycle.totalNoOfClasses || 0}
      </ArgonTypography>
    ),
    "Completed Classes": (
      <ArgonTypography variant="caption" color="text" fontWeight="medium">
        {cycle.completedClasses || 0}
      </ArgonTypography>
    ),
    Status: (
      <ArgonTypography variant="caption" color="text" fontWeight="medium">
        {cycle.status || "N/A"}
      </ArgonTypography>
    ),
  }));

  if (loading) {
    return (
      <DashboardLayout>
        <DashboardNavbar />
        <ArgonBox py={3}>
          <Card>
            <ArgonBox p={3} textAlign="center">
              <ArgonTypography variant="h6" color="text" fontWeight="medium">
                Loading invoice details...
              </ArgonTypography>
              <ArgonTypography variant="caption" color="secondary">
                Please wait while we fetch the data.
              </ArgonTypography>
            </ArgonBox>
          </Card>
        </ArgonBox>
        <Footer />
      </DashboardLayout>
    );
  }

  if (error || !invoiceData) {
    return (
      <DashboardLayout>
        <DashboardNavbar />
        <ArgonBox py={3}>
          <ArgonBox textAlign="center" py={4}>
            <ArgonTypography variant="h6" color="text" fontWeight="medium">
              {error || "Invoice not found"}
            </ArgonTypography>
            <ArgonButton
              variant="gradient"
              color="info"
              onClick={() => navigate("/creditmapping")}
              sx={{ mt: 2 }}
            >
              Back to Credit Mapping
            </ArgonButton>
          </ArgonBox>
        </ArgonBox>
        <Footer />
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <DashboardNavbar />

      <ArgonBox py={3}>
        <ArgonBox mb={3}>
          <Card>
            <ArgonBox p={3}>
              <Card
                sx={{
                  mt: 3,
                  borderRadius: "20px",
                  backgroundColor: "white",
                  boxShadow: "0 8px 24px rgba(0,0,0,0.08)",
                  border: "1px solid #f5f5f5",
                }}
              >
                <ArgonBox px={3} py={1.5}>
                  {" "}
                  {/* 👈 yaha py kam kiya */}
                  <ArgonBox
                    display="flex"
                    alignItems="center"
                    justifyContent="space-between"
                    flexDirection="row"
                    gap={4}
                  >
                    {/* Class Status */}
                    <ArgonBox display="flex" alignItems="center" flexDirection="row">
                      <ArgonTypography variant="h6" fontWeight="bold" color="dark">
                        Class Status
                      </ArgonTypography>
                      <ArgonBox
                        ml={2}
                        px={2.5}
                        py={0.5}
                        sx={{
                          background:
                            classStatus === "CREATED"
                              ? "linear-gradient(135deg, #9e9e9e, #757575)"
                              : classStatus === "SCHEDULED"
                              ? "linear-gradient(135deg, #43a047, #26a69a)"
                              : classStatus === "ONGOING"
                              ? "linear-gradient(135deg, #ff9800, #f57c00)"
                              : classStatus === "CANCELLED"
                              ? "linear-gradient(135deg, #f44336, #d32f2f)"
                              : classStatus === "COMPLETED"
                              ? "linear-gradient(135deg, #43a047, #26a69a)"
                              : classStatus === "ON_HOLD"
                              ? "linear-gradient(135deg, #ff5722, #e64a19)"
                              : "linear-gradient(135deg, #f44336, #d32f2f)",
                          borderRadius: "20px",
                          boxShadow: "0 4px 10px rgba(67,160,71,0.3)",
                        }}
                      >
                        <ArgonTypography
                          variant="caption"
                          fontWeight="bold"
                          color="white"
                          sx={{ fontSize: "13px", letterSpacing: "0.5px" }}
                        >
                          {classStatus === "SCHEDULED" ? "ACTIVE" : classStatus}
                        </ArgonTypography>
                      </ArgonBox>
                    </ArgonBox>

                    {/* Tutor Name */}
                    <ArgonBox>
                      <ArgonTypography variant="body2" fontWeight="medium" color="text">
                        Tutor’s Name
                      </ArgonTypography>
                      <ArgonTypography variant="h6" fontWeight="bold" color="dark">
                        {tutorName}
                      </ArgonTypography>
                    </ArgonBox>

                    {/* Cycle Dropdown */}
                    <ArgonBox display="flex" alignItems="center" flexDirection="row">
                      <ArgonTypography variant="body2" fontWeight="medium" mr={2}>
                        Cycle
                      </ArgonTypography>
                      <Dropdown
                        label=""
                        options={cycles.map((cycle, index) => cycle.name || `Cycle ${index + 1}`)}
                        value={cycle}
                        onChange={(e) => setCycle(e.target.value)}
                        sx={{
                          minWidth: "100px",
                          borderRadius: "12px",
                          backgroundColor: "#fafafa",
                          boxShadow: "0 2px 8px rgba(0,0,0,0.05)",
                        }}
                      />
                    </ArgonBox>
                  </ArgonBox>
                </ArgonBox>
              </Card>
            </ArgonBox>

            <ArgonBox>
              <ArgonTypography sx={{ mx: 2, mb: 3 }} variant="h5" fontWeight="medium">
                Students List
              </ArgonTypography>
              <ArgonBox
                sx={{
                  "& .MuiTableRow-root:not(:last-child)": {
                    "& td": {
                      borderBottom: ({ borders: { borderWidth, borderColor } }) =>
                        `${borderWidth[1]} solid ${borderColor}`,
                    },
                  },
                }}
              >
                <Table columns={columns} rows={rows} />
              </ArgonBox>
            </ArgonBox>

            <Card
              sx={{
                mt: 3,
                borderRadius: "16px",
                backgroundColor: "white",
                boxShadow: "0 6px 20px rgba(0,0,0,0.08)",
                border: "1px solid #f0f0f0",
              }}
            >
              <ArgonBox p={3}>
                {/* Header */}
                <ArgonBox display="flex" alignItems="center" gap={2} mb={3}>
                  <ArgonBox
                    sx={{
                      backgroundColor: "white",
                      borderRadius: "12px",
                      p: 1.2,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      boxShadow: "0 3px 8px rgba(0,0,0,0.08)",
                    }}
                  >
                    {/* <Payment sx={{ color: "#344767", fontSize: "22px" }} /> */}
                  </ArgonBox>
                  <ArgonBox>
                    <ArgonTypography variant="h5" fontWeight="bold" color="dark">
                      Package Details
                    </ArgonTypography>
                    <ArgonTypography variant="body2" color="text">
                      Pricing and session information
                    </ArgonTypography>
                  </ArgonBox>
                </ArgonBox>

                <Grid container spacing={3}>
                  {/* Hourly Rates */}
                  <Grid item xs={12} md={3}>
                    <ArgonBox
                      sx={{
                        backgroundColor: "white",
                        borderRadius: "12px",
                        p: 2.5,
                        boxShadow: "0 4px 16px rgba(0,0,0,0.06)",
                        border: "1px solid #f5f5f5",
                        height: "100%",
                      }}
                    >
                      <ArgonBox display="flex" alignItems="center" gap={1.5} mb={2}>
                        {/* <AttachMoney sx={{ color: "#4caf50", fontSize: "20px" }} /> */}
                        <ArgonTypography variant="h6" fontWeight="bold" color="dark">
                          Class Rates
                        </ArgonTypography>
                      </ArgonBox>

                      <ArgonBox>
                        <ArgonBox
                          display="flex"
                          justifyContent="space-between"
                          alignItems="center"
                          mb={1}
                        >
                          <ArgonTypography variant="body2" color="text" fontWeight="medium">
                            Fee Charge per class
                          </ArgonTypography>
                          <ArgonTypography variant="body2" fontWeight="medium" color="dark">
                            ₹ {Math.round(feeCharge)}
                          </ArgonTypography>
                        </ArgonBox>
                        <ArgonBox display="flex" justifyContent="space-between" alignItems="center">
                          <ArgonTypography variant="body2" color="text" fontWeight="medium">
                            Tutor Salary per class
                          </ArgonTypography>
                          <ArgonTypography variant="body2" fontWeight="medium" color="dark">
                            ₹ {Math.round(tutorSalary)}
                          </ArgonTypography>
                        </ArgonBox>
                      </ArgonBox>
                    </ArgonBox>
                  </Grid>

                  {/* Sessions */}
                  <Grid item xs={12} md={3}>
                    <ArgonBox
                      sx={{
                        backgroundColor: "white",
                        borderRadius: "12px",
                        p: 2.5,
                        boxShadow: "0 4px 16px rgba(0,0,0,0.06)",
                        border: "1px solid #f5f5f5",
                        height: "100%",
                      }}
                    >
                      <ArgonBox display="flex" alignItems="center" gap={1.5} mb={2}>
                        {/* <Schedule sx={{ color: "#2196f3", fontSize: "20px" }} /> */}
                        <ArgonTypography variant="h6" fontWeight="bold" color="dark">
                          Sessions
                        </ArgonTypography>
                      </ArgonBox>

                      <ArgonBox>
                        <ArgonBox
                          display="flex"
                          justifyContent="space-between"
                          alignItems="center"
                          mb={1}
                        >
                          <ArgonTypography variant="body2" color="text" fontWeight="medium">
                            Sessions in a week
                          </ArgonTypography>
                          <ArgonTypography variant="caption" fontWeight="medium" color="dark">
                            {sessionsPerWeek} sessions | {sessionsPerWeek * hoursPerSession} hours
                          </ArgonTypography>
                        </ArgonBox>
                        <ArgonBox display="flex" justifyContent="space-between" alignItems="center">
                          <ArgonTypography variant="body2" color="text" fontWeight="medium">
                            Sessions in a month
                          </ArgonTypography>
                          <ArgonTypography variant="caption" fontWeight="medium" color="dark">
                            {sessionsPerMonth} sessions | {sessionsPerMonth * hoursPerSession} hours
                          </ArgonTypography>
                        </ArgonBox>
                      </ArgonBox>
                    </ArgonBox>
                  </Grid>

                  {/* Month Package Summary */}
                  <Grid item xs={12} md={3}>
                    <ArgonBox
                      sx={{
                        backgroundColor: "white",
                        borderRadius: "12px",
                        p: 2.5,
                        boxShadow: "0 4px 16px rgba(0,0,0,0.06)",
                        border: "1px solid #f5f5f5",
                        height: "100%",
                      }}
                    >
                      <ArgonBox display="flex" alignItems="center" gap={1.5} mb={2}>
                        <ArgonTypography variant="h6" fontWeight="bold" color="dark">
                          Month Package Summary
                        </ArgonTypography>
                      </ArgonBox>

                      <ArgonBox>
                        <ArgonBox
                          display="flex"
                          justifyContent="space-between"
                          alignItems="center"
                          mb={1}
                        >
                          <ArgonTypography variant="body2" color="text" fontWeight="medium">
                            Charge
                          </ArgonTypography>
                          <ArgonTypography variant="body2" fontWeight="medium" color="dark">
                            ₹ {monthCharge}
                          </ArgonTypography>
                        </ArgonBox>
                        <ArgonBox
                          display="flex"
                          justifyContent="space-between"
                          alignItems="center"
                          mb={1}
                        >
                          <ArgonTypography variant="body2" color="text" fontWeight="medium">
                            Tutor Pay
                          </ArgonTypography>
                          <ArgonTypography variant="body2" fontWeight="medium" color="dark">
                            ₹ {monthTutorPay}
                          </ArgonTypography>
                        </ArgonBox>
                        <ArgonBox display="flex" justifyContent="space-between" alignItems="center">
                          <ArgonTypography variant="body2" color="text" fontWeight="medium">
                            Profit
                          </ArgonTypography>
                          <ArgonTypography
                            variant="body2"
                            fontWeight="bold"
                            color={
                              monthProfitPercent < 20
                                ? "error"
                                : monthProfitPercent <= 25
                                ? "warning"
                                : "success"
                            }
                            sx={{
                              color:
                                monthProfitPercent >= 20 && monthProfitPercent <= 25
                                  ? "#E6A700 !important"
                                  : undefined,
                            }}
                          >
                            ₹ {monthProfit} ({monthProfitPercent}%)
                          </ArgonTypography>
                        </ArgonBox>
                      </ArgonBox>
                    </ArgonBox>
                  </Grid>

                  {/* Total Package Summary */}
                  <Grid item xs={12} md={3}>
                    <ArgonBox
                      sx={{
                        backgroundColor: "white",
                        borderRadius: "12px",
                        p: 2.5,
                        boxShadow: "0 4px 16px rgba(0,0,0,0.06)",
                        border: "1px solid #f5f5f5",
                        height: "100%",
                      }}
                    >
                      <ArgonBox
                        display="flex"
                        alignItems="center"
                        justifyContent="space-between"
                        gap={1.5}
                        mb={2}
                      >
                        {/* <Payment sx={{ color: "#344767", fontSize: "20px" }} /> */}
                        <ArgonTypography variant="h6" fontWeight="bold" color="dark">
                          Total Package Summary
                        </ArgonTypography>
                        <ArgonTypography variant="h6" fontWeight="bold" color="dark">
                          Total Cycles: {totalCycles}
                        </ArgonTypography>
                      </ArgonBox>

                      <ArgonBox>
                        <ArgonBox
                          display="flex"
                          justifyContent="space-between"
                          alignItems="center"
                          mb={1}
                        >
                          <ArgonTypography variant="body2" color="text" fontWeight="medium">
                            Charge
                          </ArgonTypography>
                          <ArgonTypography variant="body2" fontWeight="medium" color="dark">
                            ₹ {totalCharge}
                          </ArgonTypography>
                        </ArgonBox>
                        <ArgonBox
                          display="flex"
                          justifyContent="space-between"
                          alignItems="center"
                          mb={1}
                        >
                          <ArgonTypography variant="body2" color="text" fontWeight="medium">
                            Tutor Pay
                          </ArgonTypography>
                          <ArgonTypography variant="body2" fontWeight="medium" color="dark">
                            ₹ {totalTutorPay}
                          </ArgonTypography>
                        </ArgonBox>
                        <ArgonBox display="flex" justifyContent="space-between" alignItems="center">
                          <ArgonTypography variant="body2" color="text" fontWeight="medium">
                            Profit
                          </ArgonTypography>
                          <ArgonTypography
                            variant="body2"
                            fontWeight="bold"
                            color={
                              totalProfitPercent < 20
                                ? "error"
                                : totalProfitPercent <= 25
                                ? "warning"
                                : "success"
                            }
                            sx={{
                              color:
                                totalProfitPercent >= 20 && totalProfitPercent <= 25
                                  ? "#E6A700 !important"
                                  : undefined,
                            }}
                          >
                            ₹ {totalProfit} ({totalProfitPercent}%)
                          </ArgonTypography>
                        </ArgonBox>
                      </ArgonBox>
                    </ArgonBox>
                  </Grid>
                </Grid>

                {/* Tutor Info */}
                <ArgonBox mt={3}>
                  <ArgonTypography variant="body2" fontWeight="bold" color="dark">
                    Tutor Name - {tutorName}
                  </ArgonTypography>
                </ArgonBox>
              </ArgonBox>
            </Card>

            {hasOldTutorReplacement && (
              <Card
                sx={{
                  mt: 3,
                  borderRadius: "16px",
                  backgroundColor: "white",
                  boxShadow: "0 6px 20px rgba(0,0,0,0.08)",
                  border: "1px solid #f0f0f0",
                }}
              >
                <ArgonBox p={3}>
                  <ArgonBox display="flex" alignItems="center" gap={2} mb={3}>
                    <ArgonBox>
                      <ArgonTypography variant="h5" fontWeight="bold" color="dark">
                        Old Tutor Details
                      </ArgonTypography>
                      <ArgonTypography variant="body2" color="text">
                        Split data for replaced tutor
                      </ArgonTypography>
                    </ArgonBox>
                  </ArgonBox>

                  <Grid container spacing={3}>
                    <Grid item xs={12} md={4}>
                      <ArgonBox
                        sx={{
                          backgroundColor: "white",
                          borderRadius: "12px",
                          p: 2.5,
                          boxShadow: "0 4px 16px rgba(0,0,0,0.06)",
                          border: "1px solid #f5f5f5",
                          height: "100%",
                        }}
                      >
                        <ArgonTypography variant="body2" color="text" fontWeight="medium">
                          Old Tutor Name
                        </ArgonTypography>
                        <ArgonTypography variant="h6" fontWeight="bold" color="dark">
                          {oldTutorName}
                        </ArgonTypography>
                      </ArgonBox>
                    </Grid>

                    <Grid item xs={12} md={4}>
                      <ArgonBox
                        sx={{
                          backgroundColor: "white",
                          borderRadius: "12px",
                          p: 2.5,
                          boxShadow: "0 4px 16px rgba(0,0,0,0.06)",
                          border: "1px solid #f5f5f5",
                          height: "100%",
                        }}
                      >
                        <ArgonTypography variant="body2" color="text" fontWeight="medium">
                          Old Tutor Salary / class
                        </ArgonTypography>
                        <ArgonTypography variant="h6" fontWeight="bold" color="dark">
                          ₹ {Math.round(oldTutorSalary)}
                        </ArgonTypography>
                        <ArgonTypography variant="body2" color="text" fontWeight="medium">
                          Total Salary: ₹ {Math.round(oldTutorTotalSalary)}
                        </ArgonTypography>
                      </ArgonBox>
                    </Grid>

                    <Grid item xs={12} md={4}>
                      <ArgonBox
                        sx={{
                          backgroundColor: "white",
                          borderRadius: "12px",
                          p: 2.5,
                          boxShadow: "0 4px 16px rgba(0,0,0,0.06)",
                          border: "1px solid #f5f5f5",
                          height: "100%",
                        }}
                      >
                        <ArgonTypography variant="body2" color="text" fontWeight="medium">
                          Old Tutor Margin
                        </ArgonTypography>
                        <ArgonTypography variant="h6" fontWeight="bold" color="dark">
                          {oldTutorMargin !== null ? `${oldTutorMargin}%` : "N/A"}
                        </ArgonTypography>
                      </ArgonBox>
                    </Grid>

                    <Grid item xs={12} md={4}>
                      <ArgonBox
                        sx={{
                          backgroundColor: "white",
                          borderRadius: "12px",
                          p: 2.5,
                          boxShadow: "0 4px 16px rgba(0,0,0,0.06)",
                          border: "1px solid #f5f5f5",
                          height: "100%",
                        }}
                      >
                        <ArgonTypography variant="body2" color="text" fontWeight="medium">
                          Completed Classes (Old Tutor)
                        </ArgonTypography>
                        <ArgonTypography variant="h6" fontWeight="bold" color="dark">
                          {oldTutorCompletedClasses}
                        </ArgonTypography>
                      </ArgonBox>
                    </Grid>

                    <Grid item xs={12} md={4}>
                      <ArgonBox
                        sx={{
                          backgroundColor: "white",
                          borderRadius: "12px",
                          p: 2.5,
                          boxShadow: "0 4px 16px rgba(0,0,0,0.06)",
                          border: "1px solid #f5f5f5",
                          height: "100%",
                        }}
                      >
                        <ArgonTypography variant="body2" color="text" fontWeight="medium">
                          Salary Paid (Old Tutor)
                        </ArgonTypography>
                        <Chip
                          label={oldTutorSalaryStatus}
                          size="small"
                          sx={{
                            bgcolor:
                              oldTutorSalaryStatus === "PAID"
                                ? "#4caf50"
                                : oldTutorSalaryStatus === "PARTIAL"
                                ? "#ff9800"
                                : oldTutorSalaryStatus === "UNPAID"
                                ? "#f44336"
                                : "#9e9e9e",
                            color: "white",
                            fontWeight: "600",
                            mt: 1,
                          }}
                        />
                      </ArgonBox>
                    </Grid>

                    <Grid item xs={12} md={4}>
                      <ArgonBox
                        sx={{
                          backgroundColor: "white",
                          borderRadius: "12px",
                          p: 2.5,
                          boxShadow: "0 4px 16px rgba(0,0,0,0.06)",
                          border: "1px solid #f5f5f5",
                          height: "100%",
                        }}
                      >
                        <ArgonTypography variant="body2" color="text" fontWeight="medium">
                          Tutor Replace Date
                        </ArgonTypography>
                        <ArgonTypography variant="h6" fontWeight="bold" color="dark">
                          {oldTutorReplacedDateLabel}
                        </ArgonTypography>
                      </ArgonBox>
                    </Grid>
                  </Grid>
                </ArgonBox>
              </Card>
            )}

            <Card
              sx={{
                mt: 3,
                borderRadius: "20px",
                backgroundColor: "white",
                boxShadow: "0 15px 35px rgba(0,0,0,0.08)",
                border: "1px solid #f0f0f0",
                position: "relative",
                overflow: "hidden",
              }}
            >
              {/* Decorative Background Element */}
              <ArgonBox
                sx={{
                  position: "absolute",
                  top: -40,
                  right: -40,
                  width: 120,
                  height: 120,
                  background: "linear-gradient(45deg, #667eea, #764ba2)",
                  borderRadius: "50%",
                  opacity: 0.05,
                }}
              />

              <ArgonBox p={4}>
                {/* Header */}
                <ArgonBox display="flex" alignItems="center" gap={2} mb={4}>
                  <ArgonBox
                    sx={{
                      background: "linear-gradient(45deg, #667eea, #764ba2)",
                      borderRadius: "12px",
                      p: 1.5,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      boxShadow: "0 4px 15px rgba(102, 126, 234, 0.3)",
                    }}
                  >
                    {/* <AccountBalance sx={{ color: "white", fontSize: "22px" }} /> */}
                  </ArgonBox>
                  <ArgonBox>
                    <ArgonTypography variant="h5" fontWeight="bold" color="#2c3e50">
                      Credit Counter
                    </ArgonTypography>
                    <ArgonTypography variant="body2" color="#6c757d">
                      Track credits, usage and payments
                    </ArgonTypography>
                  </ArgonBox>
                </ArgonBox>

                <Grid container spacing={4}>
                  {/* Progress Bar Section */}
                  <Grid item xs={12} md={6}>
                    <ArgonBox
                      sx={{
                        background: "#f8f9fa",
                        borderRadius: "16px",
                        p: 3,
                        textAlign: "center",
                        border: "1px solid #e9ecef",
                      }}
                    >
                      <ArgonBox
                        display="flex"
                        alignItems="center"
                        gap={1.5}
                        mb={3}
                        justifyContent="center"
                      >
                        {/* <DonutLarge sx={{ color: "#2196F3", fontSize: "20px" }} /> */}
                        <ArgonTypography variant="h6" fontWeight="bold" color="#2c3e50">
                          Usage Progress
                        </ArgonTypography>
                      </ArgonBox>

                      <ArgonBox
                        sx={{
                          width: 180,
                          height: 180,
                          margin: "0 auto",
                          mb: 2,
                        }}
                      >
                        <svg viewBox="0 0 100 100" style={{ width: "100%", height: "100%" }}>
                          <path
                            d="M 50,50 m 0,-46 a 46,46 0 1 1 0,92 a 46,46 0 1 1 0,-92"
                            strokeWidth="6"
                            fillOpacity="0"
                            style={{ stroke: "#e0e0e0" }}
                          />
                          <path
                            d="M 50,50 m 0,-46 a 46,46 0 1 1 0,92 a 46,46 0 1 1 0,-92"
                            strokeWidth="6"
                            fillOpacity="0"
                            style={{
                              stroke: safeProgress === 0 ? "transparent" : "url(#gradient)",
                              strokeDasharray: strokeDasharray,
                              strokeDashoffset: strokeDashoffset,
                              strokeLinecap: "round",
                            }}
                          />
                          <defs>
                            <linearGradient id="gradient" x1="0%" y1="0%" x2="100%" y2="0%">
                              <stop offset="0%" style={{ stopColor: "#667eea", stopOpacity: 1 }} />
                              <stop
                                offset="100%"
                                style={{ stopColor: "#764ba2", stopOpacity: 1 }}
                              />
                            </linearGradient>
                          </defs>
                          <text
                            x="50"
                            y="45"
                            textAnchor="middle"
                            alignmentBaseline="central"
                            style={{ fill: "#2c3e50", fontSize: "12px", fontWeight: "bold" }}
                          >
                            {safeProgress}%
                          </text>
                          <text
                            x="50"
                            y="58"
                            textAnchor="middle"
                            alignmentBaseline="central"
                            style={{ fill: "#6c757d", fontSize: "6px" }}
                          >
                            Used
                          </text>
                        </svg>
                      </ArgonBox>

                      <ArgonBox>
                        <ArgonTypography variant="h5" fontWeight="bold" color="#2c3e50">
                          ₹{usedAmount} / ₹{selectedCycleAmount}
                        </ArgonTypography>
                        <ArgonTypography variant="body2" color="#6c757d">
                          Credit Usage
                        </ArgonTypography>
                        <ArgonBox>
                          <ArgonTypography
                            variant="caption"
                            color="success.main"
                            fontWeight="medium"
                          >
                            {completedSessions} / {totalSessions} Classes Completed
                          </ArgonTypography>
                        </ArgonBox>
                      </ArgonBox>
                    </ArgonBox>
                  </Grid>

                  {/* Table Section */}
                  <Grid item xs={12} md={6}>
                    <ArgonBox
                      sx={{
                        background: "white",
                        borderRadius: "16px",
                        p: 3,
                        boxShadow: "0 8px 25px rgba(0,0,0,0.06)",
                        border: "1px solid #f0f0f0",
                      }}
                    >
                      <ArgonBox display="flex" alignItems="center" gap={1.5} mb={3}>
                        {/* <TableChart sx={{ color: "#4CAF50", fontSize: "20px" }} /> */}
                        <ArgonTypography variant="h6" fontWeight="bold" color="#2c3e50">
                          Credit Details
                        </ArgonTypography>
                      </ArgonBox>

                      <ArgonBox
                        sx={{
                          "& .MuiTableRow-root:not(:last-child)": {
                            "& td": {
                              borderBottom: ({ borders: { borderWidth, borderColor } }) =>
                                `${borderWidth[1]} solid ${borderColor}`,
                            },
                          },
                        }}
                      >
                        <Table columns={columns1} rows={rows1} />
                      </ArgonBox>
                    </ArgonBox>
                  </Grid>
                </Grid>

                {/* Balance Info Cards */}
                <Grid container spacing={3} mt={2}>
                  {/* Left Balance Card */}
                  <Grid item xs={12} md={6}>
                    <ArgonBox
                      sx={{
                        background: "linear-gradient(135deg, #e3f2fd 0%, #e2eaf0ff 100%)",
                        borderRadius: "16px",
                        p: 3,
                        border: "1px solid #e1f5fe",
                      }}
                    >
                      <ArgonBox display="flex" alignItems="center" gap={1.5} mb={2}>
                        {/* <AccountBalanceWallet sx={{ color: "#1976d2", fontSize: "20px" }} /> */}
                        <ArgonTypography variant="h6" fontWeight="bold" color="#1976d2">
                          Account Summary
                        </ArgonTypography>
                      </ArgonBox>

                      <ArgonBox display="flex" flexDirection="column" gap={1.5}>
                        <ArgonBox display="flex" justifyContent="space-between" alignItems="center">
                          <ArgonTypography variant="body2" color="#1565c0" fontWeight="medium">
                            Balance:
                          </ArgonTypography>
                          <ArgonTypography variant="body2" fontWeight="bold" color="#0d47a1">
                            ₹{balanceAmount}
                          </ArgonTypography>
                        </ArgonBox>

                        <ArgonBox display="flex" justifyContent="space-between" alignItems="center">
                          <ArgonTypography variant="body2" color="#1565c0" fontWeight="medium">
                            Unallocated Sessions:
                          </ArgonTypography>
                          <Chip
                            label={totalSessions - completedSessions}
                            size="small"
                            sx={{
                              bgcolor: "#1976d2",
                              color: "white",
                              fontWeight: "600",
                            }}
                          />
                        </ArgonBox>

                        <ArgonBox display="flex" justifyContent="space-between" alignItems="center">
                          <ArgonTypography variant="body2" color="#1565c0" fontWeight="medium">
                            Payment Method:
                          </ArgonTypography>
                          <Chip
                            label={selectedPaymentMethod}
                            size="small"
                            sx={{
                              bgcolor: "#4caf50",
                              color: "white",
                              fontWeight: "600",
                            }}
                          />
                        </ArgonBox>
                      </ArgonBox>
                    </ArgonBox>
                  </Grid>

                  {/* Right Balance Card */}
                  <Grid item xs={12} md={6}>
                    <ArgonBox
                      sx={{
                        background: "linear-gradient(135deg, #e8f5e8 0%, #e0ede1ff 100%)",
                        borderRadius: "16px",
                        p: 3,
                        border: "1px solid #e0f2e0",
                      }}
                    >
                      <ArgonBox display="flex" alignItems="center" gap={1.5} mb={2}>
                        {/* <Payment sx={{ color: "#388e3c", fontSize: "20px" }} /> */}
                        <ArgonTypography variant="h6" fontWeight="bold" color="#388e3c">
                          Payment Details
                        </ArgonTypography>
                      </ArgonBox>

                      <ArgonBox display="flex" flexDirection="column" gap={1.5}>
                        <ArgonBox display="flex" justifyContent="space-between" alignItems="center">
                          <ArgonTypography variant="body2" color="#2e7d32" fontWeight="medium">
                            Available Balance:
                          </ArgonTypography>
                          <ArgonTypography variant="body2" fontWeight="bold" color="#1b5e20">
                            ₹{balanceAmount}
                          </ArgonTypography>
                        </ArgonBox>

                        <ArgonBox display="flex" justifyContent="space-between" alignItems="center">
                          <ArgonTypography variant="body2" color="#2e7d32" fontWeight="medium">
                            Payment Status:
                          </ArgonTypography>
                          <Chip
                            label={selectedPaymentStatus}
                            size="small"
                            sx={{
                              bgcolor:
                                selectedPaymentStatus === "PAID" ||
                                selectedPaymentStatus === "COMPLETED"
                                  ? "#4caf50"
                                  : "#ff9800",
                              color: "white",
                              fontWeight: "600",
                            }}
                          />
                        </ArgonBox>

                        <ArgonBox display="flex" justifyContent="space-between" alignItems="center">
                          <ArgonTypography variant="body2" color="#2e7d32" fontWeight="medium">
                            Total Amount:
                          </ArgonTypography>
                          <ArgonTypography variant="body2" fontWeight="bold" color="#1b5e20">
                            ₹{selectedCycleAmount}
                          </ArgonTypography>
                        </ArgonBox>
                      </ArgonBox>
                    </ArgonBox>
                  </Grid>
                </Grid>
              </ArgonBox>
            </Card>
          </Card>
        </ArgonBox>
      </ArgonBox>
      <Footer />
    </DashboardLayout>
  );
}

export default Tables;
